import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, EmptyState, ErrorText, Modal, Spinner, Table } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { Logo } from '../../components/brand/Logo'
import { DocumentIdentityFooter } from '../../components/print/DocumentIdentityFooter'
import { clp, formatDate, formatDateTime, formatRut, formatTime, PAYMENT_METHOD_LABELS, TAX_DOCUMENT_TYPE_LABELS } from '../../utils/format'
import { upcomingFirst, type PortalAppointment } from './appointments'
import type { PortalMe } from './PatientPortalLayout'

interface PortalPayment {
  id: string
  receiptNumber: number
  amount: number
  method: string
  coverage: string
  status: string
  paidAt: string
  allocations: Array<{ amount: number; charge: { description: string } }>
}

interface PortalTaxDocument {
  id: string
  type: string
  status: string
  internalFolio: number
  siiFolio?: string | null
  description: string
  netAmount: number
  taxAmount: number
  totalAmount: number
  issuedAt?: string | null
  payment?: { receiptNumber: number; method: string; paidAt: string } | null
}

/** Estado de la pasarela de pago en línea (RF-PAY-3), resuelto en el backend. */
interface PaymentOptions {
  enabled: boolean
  gateway: string | null
  label: string | null
  message: string
}

/**
 * Cápsula "Mis próximas citas" (pedido de Chejo, minuta 15-09): en la pantalla
 * de pagos, lo primero que uno quiere saber es qué viene y cuánto cuesta.
 * Reusa la misma consulta de "Mis citas" —TanStack la comparte por su llave,
 * así que no hay una segunda ida al servidor— y muestra el abono cuando la
 * prestación lo exige, que es el único monto exigible antes de la atención.
 */
function ProximasCitasCard({ options }: { options?: PaymentOptions }) {
  const [error, setError] = useState('')

  const appointments = useQuery({
    queryKey: ['patient-appointments'],
    queryFn: async () => (await api.get<PortalAppointment[]>('/patient-portal/appointments')).data,
  })

  const payOnline = useMutation({
    mutationFn: async (appointmentId: string) =>
      (await api.post<{ redirectUrl: string }>(`/patient-portal/appointments/${appointmentId}/online-payment`)).data,
    // Cuando exista pasarela, el backend devuelve adónde ir a pagar.
    onSuccess: (data) => {
      window.location.href = data.redirectUrl
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const upcoming = upcomingFirst(appointments.data ?? []).slice(0, 5)

  return (
    <Card title="Mis próximas citas">
      <ErrorText>{error}</ErrorText>
      {appointments.isLoading ? (
        <Spinner />
      ) : upcoming.length === 0 ? (
        <EmptyState message="No tienes citas agendadas. Cuando reserves una hora, aquí verás su valor." />
      ) : (
        <ul className="space-y-2">
          {upcoming.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
              <div className="min-w-0">
                <p className="font-medium text-ink">{a.service.name}</p>
                <p className="text-sm text-gray-600">
                  {formatDate(a.startsAt)} a las {formatTime(a.startsAt)} · {a.professional.firstName}{' '}
                  {a.professional.lastName}
                </p>
                <p className="text-xs text-gray-500">
                  {a.deposit.required
                    ? `Abono para reservar: ${clp(a.deposit.amount)} · valor de la prestación ${clp(a.service.basePrice)}`
                    : `Valor referencial: ${clp(a.service.basePrice)} (según previsión y convenio)`}
                </p>
              </div>
              <Button
                variant={options?.enabled ? 'cta' : 'secondary'}
                className="shrink-0"
                disabled={!options?.enabled || payOnline.isPending}
                title={options?.enabled ? undefined : options?.message}
                onClick={() => payOnline.mutate(a.id)}
              >
                <Icon name="cashier" className="mr-1.5 inline h-4 w-4" />
                {options?.enabled ? 'Pagar en línea' : 'Pago en línea (próximamente)'}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {/* TODO-NEGOCIO: el pago en línea depende de razón social/RUT propios y
          de contratar el comercio (Webpay o Mercado Pago). El adaptador ya
          está: conectarlo no toca esta pantalla. */}
      <p className="mt-3 text-xs text-gray-400">
        {options?.message ??
          'Próximamente pago en línea. Por ahora el pago se hace en recepción (efectivo, débito, crédito o transferencia).'}
      </p>
    </Card>
  )
}

/** Pagos y boletas: comprobantes de pago + documentos tributarios del paciente. */
export function MisPagosPage() {
  const me = useOutletContext<PortalMe | null>()
  const [receipt, setReceipt] = useState<PortalPayment | null>(null)

  const paymentOptions = useQuery({
    queryKey: ['patient-payment-options'],
    queryFn: async () => (await api.get<PaymentOptions>('/patient-portal/payment-options')).data,
  })

  const payments = useQuery({
    queryKey: ['patient-payments'],
    queryFn: async () => (await api.get<PortalPayment[]>('/patient-portal/payments')).data,
  })
  const taxDocuments = useQuery({
    queryKey: ['patient-tax-documents'],
    queryFn: async () => (await api.get<PortalTaxDocument[]>('/patient-portal/tax-documents')).data,
  })

  return (
    <>
      <ProximasCitasCard options={paymentOptions.data} />

      <Card title="Mis pagos">
        {payments.isLoading ? (
          <Spinner />
        ) : (payments.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin pagos registrados." />
        ) : (
          <Table headers={['Folio', 'Fecha', 'Detalle', 'Medio', 'Monto', '']}>
            {(payments.data ?? []).map((p) => (
              <tr key={p.id}>
                <td className="px-2 py-2 font-mono text-xs">#{p.receiptNumber}</td>
                <td className="px-2 py-2 text-gray-600">{formatDateTime(p.paidAt)}</td>
                <td className="px-2 py-2">{p.allocations.map((al) => al.charge.description).join(' + ')}</td>
                <td className="px-2 py-2">{PAYMENT_METHOD_LABELS[p.method]}</td>
                <td className="px-2 py-2 font-medium">{clp(p.amount)}</td>
                <td className="px-2 py-2">
                  <Button variant="ghost" onClick={() => setReceipt(p)}>
                    Comprobante
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="Boletas y documentos tributarios">
        {taxDocuments.isLoading ? (
          <Spinner />
        ) : (taxDocuments.data?.length ?? 0) === 0 ? (
          <EmptyState message="Todavía no hay boletas emitidas a tu nombre." />
        ) : (
          <Table headers={['Documento', 'Folio', 'Fecha', 'Detalle', 'Total', '']}>
            {(taxDocuments.data ?? []).map((d) => (
              <tr key={d.id}>
                <td className="px-2 py-2">
                  {TAX_DOCUMENT_TYPE_LABELS[d.type] ?? d.type}
                  {d.status === 'VOIDED' && <Badge className="ml-2 bg-red-100 text-red-700">Anulada</Badge>}
                </td>
                <td className="px-2 py-2 font-mono text-xs">{d.siiFolio ?? `#${d.internalFolio}`}</td>
                <td className="px-2 py-2 text-gray-600">{d.issuedAt ? formatDateTime(d.issuedAt) : '—'}</td>
                <td className="px-2 py-2">{d.description}</td>
                <td className="px-2 py-2 font-medium">{clp(d.totalAmount)}</td>
                <td className="px-2 py-2">
                  {d.payment && (
                    <span className="text-xs text-gray-500">Pago #{d.payment.receiptNumber}</span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
        {/* TODO-NEGOCIO: la emisión real ante el SII depende de la razón social
            y del adaptador de facturación; hoy el folio es interno. */}
        <p className="mt-2 text-xs text-gray-400">
          El folio oficial del SII aparece cuando la boleta se emite electrónicamente. Mientras tanto se muestra el folio
          interno de la clínica.
        </p>
      </Card>

      {receipt && (
        <Modal title={`Comprobante de pago #${receipt.receiptNumber}`} onClose={() => setReceipt(null)}>
          <div className="space-y-3">
            <div className="print-document rounded-lg border border-gray-200 p-4">
              <Logo className="h-9" />
              <p className="mt-1 text-xs text-gray-500">Traumatología deportiva y salud ocupacional</p>
              <hr className="my-3" />
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Folio</dt>
                  <dd className="font-mono">#{receipt.receiptNumber}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Fecha</dt>
                  <dd>{formatDateTime(receipt.paidAt)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Paciente</dt>
                  <dd>
                    {me?.firstName} {me?.lastName} ({me ? formatRut(me.rut) : ''})
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Medio de pago</dt>
                  <dd>{PAYMENT_METHOD_LABELS[receipt.method]}</dd>
                </div>
              </dl>
              <hr className="my-3" />
              {receipt.allocations.map((al, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span>{al.charge.description}</span>
                  <span>{clp(al.amount)}</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between border-t border-gray-300 pt-2 font-semibold text-ink">
                <span>Total</span>
                <span>{clp(receipt.amount)}</span>
              </div>
              {/* Pie común de los impresos: contacto y QR para reagendar (H5). */}
              <DocumentIdentityFooter kind="clinical-order" />
            </div>
            <div className="flex gap-2 print:hidden">
              <Button variant="secondary" className="flex-1" onClick={() => window.print()}>
                <Icon name="print" className="mr-1.5 inline h-4 w-4" />
                Imprimir / guardar PDF
              </Button>
              <Button variant="ghost" onClick={() => setReceipt(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
